import React, {
  useReducer, useState, useEffect, useMemo,
} from 'react';
import {
  useForm,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { isValidPhoneNumber } from 'react-phone-number-input';

import { reducer, initialState, SoleraAccountCreationStep } from './soleraAccountCreationState';
import ConfirmAccountInfo from './steps/ConfirmAccountInfo';
import EmailVerification from './steps/EmailVerification';
import CreatePassword from './steps/CreatePassword';
import {
  ReadyToStartSleepioWellness,
} from '../SleepioWellnessOnboarding/steps/ReadyToStartSleepioWellness/ReadyToStartSleepioWellness';

import { PASSWORD_VALIDATION_REGEX } from '@/utils/constants';

const INITIAL_FORM_DATA = {
  email: '',
  phoneNumber: '',
  agreeToReceiveTextMessages: false,
  termsAndPrivacy: false,
  password: '',
  confirmPassword: '',
};

type SoleraAccountCreationDataMap = Record<keyof typeof INITIAL_FORM_DATA, string | boolean>;

const createSchema = (errorMap: SoleraAccountCreationDataMap) => z.object({
  email: z.string({ required_error: errorMap.email as string })
    .email({ message: errorMap.email as string }),
  phoneNumber: z.string({ required_error: errorMap.phoneNumber as string })
    .refine(
      (v) => isValidPhoneNumber(v || ''),
      { message: errorMap.phoneNumber as string },
    ),
  agreeToReceiveTextMessages: z.boolean().refine((value) => value === true, {
    message: errorMap.agreeToReceiveTextMessages as string,
  }),
  termsAndPrivacy: z.boolean().refine((value) => value === true, {
    message: errorMap.termsAndPrivacy as string,
  }),
});

type SoleraAccountCreationData = z.infer<ReturnType<typeof createSchema>>;

const SoleraAccountCreation = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [careTeamUid, setCareTeamUid] = useState<string | null>(null);
  const { t } = useTranslation();
  const location = useLocation();

  // Check if user is returning with a verification token
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');
  const careTeamUidFromUrl = searchParams.get('careTeamUid');
  const passwordSchema = z.object({
    password: z.string({ required_error: t('coverage.errors.password_rules_error') })
      .regex(PASSWORD_VALIDATION_REGEX, { message: t('coverage.errors.password_rules_error') }),
    confirmPassword: z.string({ required_error: t('coverage.errors.confirm_password_error') }),
  });
  type SoleraPasswordData = z.infer<typeof passwordSchema>;
  useEffect(() => {
    // If token is present in URL, navigate to EmailVerification step
    if (token && (careTeamUidFromUrl || careTeamUid)) {
      if (careTeamUidFromUrl) {
        setCareTeamUid(careTeamUidFromUrl);
      }
      if (state.step !== 'EmailVerification') {
        dispatch({ type: 'SET_STEP', step: 'EmailVerification' });
      }
    }
  }, [token, careTeamUidFromUrl, careTeamUid, state.step]);

  const errorMap: SoleraAccountCreationDataMap = {
    email: t('coverage.errors.email_error'),
    phoneNumber: t('coverage.sleepio.patient.please_enter_valid_phone_number'),
    agreeToReceiveTextMessages: t('coverage.sleepio.patient.agree_to_text_messages'),
    termsAndPrivacy: t('solera.account.terms_error'),
    password: '',
    confirmPassword: '',
  };

  const baseSchema = createSchema(errorMap);

  // Merge the schema dynamically based on the step
  const currentSchema = state.step === 'CreatePassword'
    ? baseSchema.merge(passwordSchema).refine((data) => data.password === data.confirmPassword, {
      message: t('coverage.errors.confirm_password_error'),
      path: ['confirmPassword'],
    })
    : baseSchema;

  const methods = useForm<SoleraAccountCreationData & SoleraPasswordData>({
    defaultValues: {
      ...INITIAL_FORM_DATA,
      password: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(currentSchema, {}, { mode: 'async' }),
  });

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<SoleraAccountCreationData & SoleraPasswordData> = (data) => {
    console.log(data);
  };

  const renderStep = () => {
    switch (state.step) {
    case 'ConfirmAccountInfo':
      return (
        <ConfirmAccountInfo
          onNext={(step: SoleraAccountCreationStep, data?: { careTeamUid: string; token?: string }) => {
            if (data) {
              setCareTeamUid(data.careTeamUid);
              if (data.token) {
                dispatch({ type: 'SET_TOKEN', token: data.token });
              }
            }
            dispatch({ type: 'SET_STEP', step });
          }}
        />
      );
    case 'EmailVerification':
      return (
        <EmailVerification
          careTeamUid={careTeamUid || ''}
          onNext={(step: SoleraAccountCreationStep, data?: { careTeamUid: string; token?: string }) => {
            if (data) {
              setCareTeamUid(data.careTeamUid);
              if (data.token) {
                dispatch({ type: 'SET_TOKEN', token: data.token });
              }
            }
            dispatch({ type: 'SET_STEP', step });
          }}
          onError={() => dispatch({ type: 'SET_STEP', step: 'Error' })}
        />
      );
    case 'CreatePassword':
      return (
        <CreatePassword
          careTeamUid={careTeamUid || ''}
          token={state.token || ''}
          onNext={(newAuthToken: string) => {
            setAuthToken(newAuthToken);
            dispatch({ type: 'SET_STEP', step: 'ReadyToStart' });
          }}
          onError={() => dispatch({ type: 'SET_STEP', step: 'Error' })}
        />
      );
    case 'ReadyToStart':
      return authToken ? <ReadyToStartSleepioWellness authToken={authToken} /> : null;
    case 'Error':
      return <div>An error occurred. Please try again.</div>;
    default:
      return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderStep()}
      </form>
    </FormProvider>
  );
};

export default SoleraAccountCreation;
